'use client';

import { ResponsiveCarousel } from '@packages/shared/src/components/Carousel/ResponsiveCarousel';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import { ExtendedRecoCardItem } from '../ExtendedRecoCardItem/ExtendedRecoCardItem.client';
import { ExtendedRecoLink } from '../ExtendedRecoLink/ExtendedRecoLink.client';
import { recoCarouselMessages } from './messages';

export type RecoCarouselProps = {
  /** Recommendation items */
  children: ReactNode;
  /** Displayed title above the items */
  title: string;
  /** Background color */
  backgroundColor?: string;
  /**
   * Either the `ExtendedRecoLink` or `ExtendedRecoCardItem` component
   */
  extendedSlot?: ReactNode;
} & ComponentProps<typeof ResponsiveCarousel>;

/**
 * Base component for Product / Category / Brand sliders
 * */
export const RecoCarousel = ({
  children,
  title,
  loadingState,
  backgroundColor = '',
  extendedSlot,
  ...rest
}: RecoCarouselProps) => {
  const { formatMessage } = useIntl();

  const carouselItems = React.Children.toArray(children);

  // If the extended slot is provided as `ExtendedRecoCardItem`, we add it to be displayed the end of the carousel items.
  if (React.isValidElement(extendedSlot) && extendedSlot.type === ExtendedRecoCardItem) {
    carouselItems.push(extendedSlot);
  }

  return (
    <div>
      {title.length > 0 && (
        <header className="text-size-h4 lg:text-size-h3 my-2 px-1.5 lg:px-2">
          {loadingState ? (
            <Skeleton className="w-1/4" />
          ) : (
            // bearer:disable javascript_react_dangerously_set_inner_html
            <span dangerouslySetInnerHTML={{ __html: title }} />
          )}
        </header>
      )}
      <div
        className="bg-color-grey-light space-y-1.5 p-1.5 pb-1.5 lg:p-2 lg:pb-2"
        // Check this https://stackoverflow.com/questions/72481680/tailwinds-background-color-is-not-being-applied-when-added-dynamically
        style={{ backgroundColor }}
      >
        <ResponsiveCarousel
          {...rest}
          aria={{
            buttonPrev: formatMessage(recoCarouselMessages.ariaPrevButton),
            buttonNext: formatMessage(recoCarouselMessages.ariaNextButton),
          }}
          showDots={false}
          hasOffset
          mobile={{ visibleSlides: 2.5, gap: 1.5 }}
          tablet={{ visibleSlides: 3.5, gap: 1.5 }}
          desktop={{
            visibleSlides: 5.5,
            slidesToScroll: 5,
            gap: 1.5,
          }}
          emblaOptions={{ dragFree: true }}
        >
          {carouselItems.map((item, index) => (
            // Using index as key is not recommended, but in this case, we assume items are static and won't change order between renders.
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>{item}</React.Fragment>
          ))}
        </ResponsiveCarousel>
        {/* Render the extended slot if it's the `ExtendedRecoLink` */}
        {React.isValidElement(extendedSlot) &&
          extendedSlot.type === ExtendedRecoLink &&
          extendedSlot}
      </div>
    </div>
  );
};
